import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Wrench, Trash2, Printer } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import RepairEstimateDialog from '../components/RepairEstimateDialog';
import { printRepairEstimate } from '../utils/repairSlipPrint';

const inr = (n) => `₹${Math.abs(Math.round(Number(n) || 0)).toLocaleString('en-IN')}`;

export default function RepairEstimates() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [forceNew, setForceNew] = useState(false);
  const [reviseFrom, setReviseFrom] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/repair-estimates');
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load estimates');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForceNew(false); setReviseFrom(null); setEditing(null); setOpen(true); };
  const openEdit = (est) => { setForceNew(false); setReviseFrom(null); setEditing(est); setOpen(true); };

  const revise = async (src) => {
    let rate = null;
    try {
      const gp = await api.get('/gold-prices/latest');
      if (gp.data?.exists && gp.data.prices) rate = gp.data.prices['22K'] || Object.values(gp.data.prices)[0];
    } catch (e) { /* noop */ }
    const WEIGHT_TYPES = ['new_gold', 'old_gold'];
    const bump = (rows = []) => rows.map((r) => (WEIGHT_TYPES.includes(r.type) && rate ? { ...r, rate } : r));
    setEditing({ ...src, gold_rate: rate ?? src.gold_rate, charges: bump(src.charges), credits: bump(src.credits) });
    setReviseFrom(src.id); setForceNew(true); setOpen(true);
  };

  const reopen = async (est) => {
    try {
      await api.post(`/repair-estimates/${est.id}/reopen`);
      toast.success('Reopened for editing');
      await load();
      setForceNew(false); setReviseFrom(null); setEditing({ ...est, status: 'draft' }); setOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reopen'); }
  };

  const remove = async (est) => {
    if (!window.confirm('Delete this repair estimate?')) return;
    try {
      await api.delete(`/repair-estimates/${est.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="repair-estimates-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100" data-testid="repair-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-teal-600" /> Repair Estimates
            </h1>
            <p className="text-sm text-gray-500">Two-sided add/remove ledger — what the customer pays or gets refunded.</p>
          </div>
        </div>
        <Button onClick={openNew} className="bg-teal-600 hover:bg-teal-700" data-testid="new-repair-estimate-btn">
          <Plus className="w-4 h-4 mr-1" /> New estimate
        </Button>
      </div>

      {items.some((e) => e.status === 'superseded') && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setShowHistory((v) => !v)} className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100" data-testid="show-history-toggle">
            {showHistory ? 'Hide superseded' : `Show history (${items.filter((e) => e.status === 'superseded').length} superseded)`}
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="repair-empty">
          <Wrench className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No repair estimates yet.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.filter((est) => showHistory || est.status !== 'superseded').map((est) => (
            <div key={est.id} className={`bg-white border rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow ${est.status === 'superseded' ? 'opacity-70' : ''}`}
              data-testid="repair-estimate-card">
              <button className="text-left flex-1" onClick={() => openEdit(est)}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-400">{est.estimate_number || est.id.slice(-6)}</span>
                  <span className={`font-bold ${est.customer_pays ? 'text-emerald-700' : 'text-red-600'}`}>
                    {est.customer_pays ? 'Customer pays' : 'We refund'} {inr(est.net_amount)}
                  </span>
                  {est.status === 'active'
                    ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                    : est.status === 'superseded'
                    ? <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Superseded</span>
                    : <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Draft</span>}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {est.order_number ? `Order ${est.order_number} · ` : 'Unlinked · '}
                  Charges {inr(est.subtotal_charges)} · GST {est.gst_percent}% · Credits {inr(est.subtotal_credits)}
                </p>
                <p className="text-xs text-gray-400">By {est.created_by_name}</p>
              </button>
              <div className="flex items-center gap-1">
                {est.status === 'active' && (
                  <>
                    <button onClick={() => revise(est)} className="text-xs px-2 py-1 rounded-full bg-teal-600 text-white hover:bg-teal-700" data-testid="revise-repair-estimate">Revise</button>
                    <button onClick={() => reopen(est)} className="text-xs px-2 py-1 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100" data-testid="reopen-repair-estimate">Reopen</button>
                  </>
                )}
                <button onClick={() => printRepairEstimate(est)} className="text-gray-400 hover:text-gray-700 p-2" title="Print slip" data-testid="print-repair-estimate">
                  <Printer className="w-4 h-4" />
                </button>
                <button onClick={() => remove(est)} className="text-red-400 hover:text-red-600 p-2" data-testid="delete-repair-estimate">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <RepairEstimateDialog
          open={open}
          onOpenChange={setOpen}
          orderId={editing?.order_id || null}
          orderNumber={editing?.order_number || null}
          estimate={editing}
          forceNew={forceNew}
          reviseFrom={reviseFrom}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
